import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import {
    ArrowLeft,
    Check,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    Copy,
    ExternalLink,
    FolderTree,
    KeyRound,
    Link2,
    Loader2,
    Plug,
    RefreshCw,
    Server,
    ShieldCheck,
    Trash2,
    Unplug,
} from "lucide-react";
import { Card, CardContent } from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { Checkbox } from "../../../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../../../ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../../ui/dialog";
import { useToast } from "../../../../hooks/use-toast";
import { useTenants } from "../../../../hooks/useTenants";
import EmptyStateCard from "../../EmptyStateCard";
import EndpointActionButton from "../../Endpoints/EndpointActionButton";
import "./SaasApps.css";
import { launchConnect, saasApi, saasErrorMessage } from "./saasApi";
import { SaasAppRegistration, SaasConnection, SaasProvider, SaasScopeOption } from "./saasTypes";
import {
    accountDisplay,
    connectionHealth,
    formatDateTime,
    formatRelative,
    humanize,
    LOG_SOURCE_LABELS,
    PAGE_SIZE,
    PROVIDERS,
} from "./saasFormat";
import { KeyValue, ListToolbar, MetaTag, SectionCard, StatusPill, useSaasTenant } from "./SaasUi";

const STEPS: Record<SaasProvider, { title: string; text: string }[]> = {
    github: [
        { title: "Enter your organization", text: "Type the GitHub organization to monitor and click Connect." },
        { title: "Create the app", text: "GitHub opens a private, read-only WhiteSwan app for your organization. Click Create." },
        { title: "Install", text: "Choose all or selected repositories. Sync starts and 30 days of history is collected." },
    ],
    gitlab: [
        { title: "Create an application", text: "In GitLab, add an application with the redirect URL and scopes shown below." },
        { title: "Paste its credentials", text: "Copy the Application ID and Secret into WhiteSwan and click Connect." },
        { title: "Authorize", text: "Approve read-only access. Sync starts and 30 days of activity is collected." },
    ],
};

interface ConnectTarget {
    instanceUrl?: string;
    organization?: string;
}

export default function SaasConnectSettings({ provider, onBack }: { provider: SaasProvider; onBack: () => void }) {
    const meta = PROVIDERS[provider];
    const ProviderIcon = meta.icon;
    const { toast } = useToast();
    const defaultTenant = useSaasTenant();
    const { tenants } = useTenants();
    const [tenantName, setTenantName] = useState("");
    const [organization, setOrganization] = useState("");
    const [page, setPage] = useState(1);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [disconnectTarget, setDisconnectTarget] = useState<SaasConnection | null>(null);
    const [scopeTarget, setScopeTarget] = useState<SaasConnection | null>(null);
    const oauthCardRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!tenantName && defaultTenant) setTenantName(defaultTenant);
    }, [defaultTenant, tenantName]);

    const connectionsQuery = useQuery(["saas-connections", provider, tenantName], () => saasApi.connections(provider, tenantName), {
        enabled: Boolean(tenantName),
        refetchInterval: (data) => ((data ?? []).some((connection) => connectionHealth(connection).tone === "working") ? 10_000 : false),
    });
    const registrationsQuery = useQuery(["saas-app-registrations", provider, tenantName], () => saasApi.appRegistrations(provider, tenantName), {
        enabled: Boolean(tenantName),
    });
    const gitlabSetupQuery = useQuery(["saas-gitlab-setup", tenantName], () => saasApi.gitlabSetupInfo(tenantName), {
        enabled: Boolean(tenantName) && provider === "gitlab",
    });

    const connections = connectionsQuery.data ?? [];
    const totalPages = Math.max(1, Math.ceil(connections.length / PAGE_SIZE));
    const pageItems = connections.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const notifyError = (error: unknown, fallback: string) => {
        toast({ title: fallback, description: saasErrorMessage(error, "Please try again."), variant: "destructive" });
    };

    const connect = useMutation(
        (target: ConnectTarget) => saasApi.connectUrl(provider, tenantName, target.instanceUrl, target.organization),
        {
            onSuccess: (response) => launchConnect(response),
            onError: (error) => notifyError(error, `Unable to start the ${meta.label} connection`),
        },
    );

    const sync = useMutation((connection: SaasConnection) => saasApi.syncConnection(provider, tenantName, connection.id), {
        onSuccess: (response) => {
            toast({ title: response.status === "STARTED" ? "Sync started" : "Sync already running", description: response.message });
            connectionsQuery.refetch();
        },
        onError: (error) => notifyError(error, "Unable to start the sync"),
    });

    const disconnect = useMutation((connection: SaasConnection) => saasApi.disconnect(provider, tenantName, connection.id), {
        onSuccess: (response) => {
            toast({ title: "Disconnected", description: response.message });
            setDisconnectTarget(null);
            connectionsQuery.refetch();
        },
        onError: (error) => notifyError(error, "Unable to disconnect"),
    });

    const tenantOptions = useMemo(() => {
        const names = new Set(tenants.map((tenant) => tenant.tenantName).filter(Boolean));
        if (defaultTenant) names.add(defaultTenant);
        return [...names];
    }, [tenants, defaultTenant]);

    const gitlabReady = Boolean(gitlabSetupQuery.data?.applicationRegistered);

    const headerConnect = () => {
        if (provider === "github") {
            connect.mutate({ organization: organization.trim() || undefined });
        } else if (gitlabReady) {
            connect.mutate({});
        } else {
            oauthCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            oauthCardRef.current?.querySelector<HTMLInputElement>("input[data-autofocus]")?.focus();
        }
    };

    const reconnect = (connection: SaasConnection) =>
        connect.mutate({
            instanceUrl: connection.instanceUrl,
            organization: provider === "github" && connection.accountType === "ORGANIZATION" ? connection.accountLogin ?? undefined : undefined,
        });

    return (
        <div className="tenant-settings-page flex-1 space-y-6 p-6">
            <Card className="settings-header-card border-border/40 rounded-2xl">
                <CardContent className="p-5 sm:p-6">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="icon" className="settings-back-button" onClick={onBack} aria-label="Back to settings"
                                title="Back to settings">
                            <ArrowLeft className="w-4 h-4" />
                        </Button>
                        <div className="settings-icon-frame w-12 h-12 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 rounded-xl flex items-center justify-center border border-blue-200 dark:border-blue-700">
                            <ProviderIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="flex-1 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                            <div>
                                <h1 className="text-2xl font-bold tracking-tight" data-testid="text-page-title">
                                    {meta.label}
                                </h1>
                                <p className="text-muted-foreground">
                                    Connect {meta.label} to monitor identities, access and audit activity
                                </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-3">
                                {tenantOptions.length > 1 && (
                                    <Select value={tenantName} onValueChange={setTenantName}>
                                        <SelectTrigger className="saas-control w-56" data-testid="select-saas-tenant">
                                            <SelectValue placeholder="Select tenant" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {tenantOptions.map((name) => (
                                                <SelectItem key={name} value={name}>
                                                    {name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                                <EndpointActionButton className="h-[42px]" onClick={headerConnect} disabled={!tenantName || connect.isLoading}
                                                      data-testid={`button-connect-${provider}`}>
                                    {connect.isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />}
                                    Connect {meta.label}
                                </EndpointActionButton>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <SectionCard
                    className="xl:col-span-2"
                    title={`Connect ${meta.label} in three steps`}
                    subtitle={provider === "github"
                        ? "WhiteSwan creates a private GitHub App with read-only permissions for your organization"
                        : "Uses read-only OAuth scopes. Nothing is ever written to GitLab"}
                    icon={<ShieldCheck className="w-4 h-4" />}
                >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {STEPS[provider].map((step, index) => (
                            <Card key={step.title} className="cloud-list-card rounded-xl">
                                <CardContent className="p-4">
                                    <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center mb-3">
                                        {index + 1}
                                    </div>
                                    <div className="font-semibold text-sm mb-1">{step.title}</div>
                                    <p className="text-xs text-muted-foreground leading-relaxed">{step.text}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                    {provider === "github" && (
                        <form
                            className="mt-4 flex flex-col sm:flex-row gap-3"
                            onSubmit={(event) => {
                                event.preventDefault();
                                headerConnect();
                            }}
                        >
                            <Input
                                className="saas-control sm:max-w-sm"
                                placeholder="Organization, e.g. acme-corp"
                                value={organization}
                                onChange={(event) => setOrganization(event.target.value)}
                                data-testid="input-github-organization"
                            />
                            <EndpointActionButton type="submit" className="h-[42px]" disabled={!tenantName || connect.isLoading}>
                                {connect.isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />}
                                Connect
                            </EndpointActionButton>
                            <span className="text-xs text-muted-foreground self-center">Leave empty to connect your personal account.</span>
                        </form>
                    )}
                </SectionCard>

                {provider === "gitlab" ? (
                    <GitlabTokenCard tenantName={tenantName} onConnected={() => connectionsQuery.refetch()} />
                ) : (
                    <SectionCard title="Permissions WhiteSwan requests" icon={<KeyRound className="w-4 h-4" />}>
                        <ul className="space-y-2 text-xs text-muted-foreground">
                            {[
                                "Members (read): organization members, owners, teams and outside collaborators",
                                "Administration (read): repository collaborators and branch protection",
                                "Organization administration (read): audit log on GitHub Enterprise Cloud",
                                "Metadata (read): repositories, visibility and activity events",
                            ].map((item) => (
                                <li key={item} className="flex gap-2">
                                    <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </SectionCard>
                )}
            </div>

            {provider === "gitlab" && (
                <div ref={oauthCardRef}>
                    <GitlabOAuthCard
                        tenantName={tenantName}
                        onSaved={() => {
                            gitlabSetupQuery.refetch();
                            registrationsQuery.refetch();
                        }}
                        onConnect={(instanceUrl) => connect.mutate({ instanceUrl })}
                        connecting={connect.isLoading}
                    />
                </div>
            )}

            <div className="space-y-2">
                <ListToolbar page={page} totalPages={totalPages} onPageChange={setPage} total={connections.length} pageSize={PAGE_SIZE}
                             label="Connected account pages">
                    <span className="text-sm font-semibold pl-1">Connected accounts</span>
                    <Button variant="outline" className="saas-outline-button" onClick={() => connectionsQuery.refetch()}>
                        <RefreshCw className={`w-4 h-4 ${connectionsQuery.isFetching ? "animate-spin" : ""}`} />
                        Refresh
                    </Button>
                </ListToolbar>

                {connectionsQuery.isLoading ? (
                    <Card className="cloud-card-surface rounded-2xl animate-pulse h-28" />
                ) : connections.length === 0 ? (
                    <EmptyStateCard
                        icon={Unplug}
                        title={`No ${meta.label} accounts connected`}
                        description={`Connect ${meta.label} to start syncing identities, access and audit activity.`}
                        className="cloud-card-surface border-dashed rounded-2xl"
                    />
                ) : (
                    pageItems.map((connection) => (
                        <ConnectionCard
                            key={connection.id}
                            provider={provider}
                            connection={connection}
                            syncing={sync.isLoading && sync.variables?.id === connection.id}
                            onSync={() => sync.mutate(connection)}
                            onDisconnect={() => setDisconnectTarget(connection)}
                            onReconnect={() => reconnect(connection)}
                            onManageScope={provider === "gitlab" ? () => setScopeTarget(connection) : undefined}
                        />
                    ))
                )}
            </div>

            <Card className="cloud-card-surface rounded-2xl">
                <button
                    type="button"
                    className="w-full flex items-center justify-between px-5 py-4 text-left"
                    onClick={() => setShowAdvanced((value) => !value)}
                    data-testid="button-toggle-advanced"
                >
                    <span className="flex items-center gap-3">
                        <span className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                            <Server className="w-4 h-4" />
                        </span>
                        <span>
                            <span className="block font-semibold text-sm">
                                {provider === "github" ? "GitHub Enterprise Server and created apps" : "Registered GitLab applications"}
                            </span>
                            <span className="block text-xs text-muted-foreground">
                                {provider === "github"
                                    ? "Connect a self-hosted GitHub instance or remove apps WhiteSwan created"
                                    : "OAuth applications saved for gitlab.com or self-managed instances"}
                            </span>
                        </span>
                    </span>
                    {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showAdvanced && (
                    <CardContent className="pt-0 px-5 pb-5 border-t border-border">
                        <AdvancedSection
                            provider={provider}
                            tenantName={tenantName}
                            registrations={registrationsQuery.data ?? []}
                            onChanged={() => {
                                registrationsQuery.refetch();
                                gitlabSetupQuery.refetch();
                            }}
                            onConnect={(target) => connect.mutate(target)}
                            connecting={connect.isLoading}
                        />
                    </CardContent>
                )}
            </Card>

            <AlertDialog open={Boolean(disconnectTarget)} onOpenChange={(open) => !open && setDisconnectTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Disconnect {disconnectTarget ? accountDisplay(disconnectTarget) : ""}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Monitoring stops and all synced identities, access data, audit logs and findings for this account are deleted.
                            {provider === "github" && " Uninstall the WhiteSwan app from the organization's GitHub settings to revoke access completely."}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={(event) => {
                                event.preventDefault();
                                if (disconnectTarget) disconnect.mutate(disconnectTarget);
                            }}
                            disabled={disconnect.isLoading}
                        >
                            {disconnect.isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                            Disconnect
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {scopeTarget && (
                <GitlabScopeDialog
                    tenantName={tenantName}
                    connection={scopeTarget}
                    onClose={() => setScopeTarget(null)}
                    onSaved={() => {
                        setScopeTarget(null);
                        connectionsQuery.refetch();
                    }}
                />
            )}
        </div>
    );
}

function CopyField({ label, value }: { label: string; value: string }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            setCopied(false);
        }
    };
    return (
        <div className="space-y-1.5">
            <Label>{label}</Label>
            <div className="flex gap-2">
                <Input readOnly value={value} className="saas-control font-mono text-xs" onFocus={(event) => event.target.select()} />
                <Button type="button" variant="outline" size="icon" className="saas-icon-button flex-shrink-0" onClick={copy} aria-label={`Copy ${label}`} title={`Copy ${label}`}>
                    {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                </Button>
            </div>
        </div>
    );
}

function GitlabOAuthCard({ tenantName, onSaved, onConnect, connecting }: {
    tenantName: string;
    onSaved: () => void;
    onConnect: (instanceUrl?: string) => void;
    connecting: boolean;
}) {
    const { toast } = useToast();
    const [instanceUrl, setInstanceUrl] = useState("");
    const [clientId, setClientId] = useState("");
    const [clientSecret, setClientSecret] = useState("");
    const [editing, setEditing] = useState(false);
    const normalizedInstance = instanceUrl.trim() || undefined;
    const setupQuery = useQuery(["saas-gitlab-setup", tenantName, normalizedInstance ?? "cloud"],
        () => saasApi.gitlabSetupInfo(tenantName, normalizedInstance), { enabled: Boolean(tenantName), keepPreviousData: true });
    const setup = setupQuery.data;
    const registered = Boolean(setup?.applicationRegistered) && !editing;

    const save = useMutation(
        () => saasApi.saveAppRegistration("gitlab", tenantName, { instanceUrl: normalizedInstance, clientId: clientId.trim(), clientSecret: clientSecret.trim() }),
        {
            onSuccess: () => {
                setClientSecret("");
                setEditing(false);
                setupQuery.refetch();
                onSaved();
                onConnect(normalizedInstance);
            },
            onError: (error) => {
                toast({ title: "Unable to save the GitLab application", description: saasErrorMessage(error, "Check the details."), variant: "destructive" });
            },
        },
    );

    return (
        <SectionCard
            title="Connect with a GitLab OAuth application"
            subtitle="Recommended for gitlab.com and self-managed GitLab"
            icon={<Link2 className="w-4 h-4" />}
            actions={setup && (
                <Button variant="outline" className="saas-small-button" asChild>
                    <a href={setup.applicationsUrl} target="_blank" rel="noreferrer">
                        <ExternalLink className="w-4 h-4" />
                        Open GitLab applications
                    </a>
                </Button>
            )}
        >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-3">
                    <div className="space-y-1.5">
                        <Label htmlFor="gitlab-oauth-instance">GitLab URL</Label>
                        <Input id="gitlab-oauth-instance" className="saas-control" placeholder="https://gitlab.com" value={instanceUrl}
                               onChange={(event) => setInstanceUrl(event.target.value)} />
                    </div>
                    {setup ? (
                        <>
                            <CopyField label="Redirect URI" value={setup.redirectUri} />
                            <CopyField label="Scopes" value={setup.scopes} />
                            <p className="text-xs text-muted-foreground">Keep "Confidential" checked when you create the application.</p>
                        </>
                    ) : (
                        <div className="h-32 rounded-xl border border-border bg-card animate-pulse" />
                    )}
                </div>
                <div className="space-y-3">
                    {registered ? (
                        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                            <div className="flex items-center gap-2 text-sm font-medium">
                                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                Application saved for {setup?.instanceUrl.replace(/^https?:\/\//, "")}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <EndpointActionButton className="h-[42px]" onClick={() => onConnect(normalizedInstance)} disabled={connecting}>
                                    {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />}
                                    Connect GitLab
                                </EndpointActionButton>
                                <Button variant="outline" className="saas-outline-button" onClick={() => setEditing(true)}>Replace credentials</Button>
                            </div>
                        </div>
                    ) : (
                        <form
                            className="space-y-3"
                            onSubmit={(event) => {
                                event.preventDefault();
                                save.mutate();
                            }}
                        >
                            <div className="space-y-1.5">
                                <Label htmlFor="gitlab-client-id">Application ID</Label>
                                <Input id="gitlab-client-id" data-autofocus className="saas-control" value={clientId} onChange={(event) => setClientId(event.target.value)} />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="gitlab-client-secret">Secret</Label>
                                <Input id="gitlab-client-secret" type="password" autoComplete="off" className="saas-control" value={clientSecret}
                                       onChange={(event) => setClientSecret(event.target.value)} />
                            </div>
                            <div className="flex gap-2">
                                <EndpointActionButton type="submit" className="h-[42px]" disabled={!tenantName || !clientId.trim() || !clientSecret.trim() || save.isLoading}>
                                    {save.isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />}
                                    Save and connect
                                </EndpointActionButton>
                                {editing && <Button type="button" variant="outline" className="saas-outline-button" onClick={() => setEditing(false)}>Cancel</Button>}
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </SectionCard>
    );
}

function AdvancedSection({ provider, tenantName, registrations, onChanged, onConnect, connecting }: {
    provider: SaasProvider;
    tenantName: string;
    registrations: SaasAppRegistration[];
    onChanged: () => void;
    onConnect: (target: ConnectTarget) => void;
    connecting: boolean;
}) {
    const { toast } = useToast();
    const [instanceUrl, setInstanceUrl] = useState("");
    const [organization, setOrganization] = useState("");
    const remove = useMutation((registrationId: number) => saasApi.deleteAppRegistration(provider, tenantName, registrationId), {
        onSuccess: () => onChanged(),
        onError: (error) => {
            toast({ title: "Unable to remove the application", description: saasErrorMessage(error, "Try again."), variant: "destructive" });
        },
    });

    return (
        <div className={`grid grid-cols-1 ${provider === "github" ? "lg:grid-cols-2" : ""} gap-6 pt-4`}>
            {provider === "github" && (
                <form
                    className="space-y-3"
                    onSubmit={(event) => {
                        event.preventDefault();
                        onConnect({ instanceUrl: instanceUrl.trim(), organization: organization.trim() || undefined });
                    }}
                >
                    <p className="text-xs text-muted-foreground leading-relaxed">
                        WhiteSwan creates the app on your GitHub Enterprise Server the same way as on github.com. The server must be reachable from your browser.
                    </p>
                    <div className="space-y-1.5">
                        <Label>GitHub Enterprise Server URL</Label>
                        <Input className="saas-control" placeholder="https://github.example.com" value={instanceUrl} onChange={(event) => setInstanceUrl(event.target.value)} />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Organization</Label>
                        <Input className="saas-control" placeholder="acme-corp" value={organization} onChange={(event) => setOrganization(event.target.value)} />
                    </div>
                    <EndpointActionButton type="submit" className="h-[42px]" disabled={!tenantName || !instanceUrl.trim() || connecting}>
                        {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />}
                        Connect
                    </EndpointActionButton>
                </form>
            )}
            <div className="space-y-2">
                <div className="text-sm font-semibold">{provider === "github" ? "Apps created by WhiteSwan" : "Saved applications"}</div>
                {registrations.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Nothing saved yet.</p>
                ) : (
                    registrations.map((registration) => (
                        <Card key={registration.id} className="cloud-list-card rounded-xl">
                            <CardContent className="p-3 flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-sm font-medium truncate">
                                        {provider === "github" ? registration.appName ?? registration.appSlug : registration.instanceUrl}
                                    </div>
                                    <div className="text-xs text-muted-foreground truncate">
                                        {provider === "github"
                                            ? `${registration.ownerLogin} · ${registration.instanceUrl.replace(/^https?:\/\//, "")}`
                                            : `Application ${registration.clientId}`}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" className="saas-small-button" disabled={connecting}
                                            onClick={() => onConnect({
                                                instanceUrl: registration.instanceUrl,
                                                organization: provider === "github" ? registration.ownerLogin : undefined,
                                            })}>
                                        <ExternalLink className="w-4 h-4" />
                                        Connect
                                    </Button>
                                    <Button size="icon" variant="outline" className="saas-small-button w-[34px] px-0 text-destructive" aria-label="Remove application"
                                            onClick={() => remove.mutate(registration.id)}>
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>
        </div>
    );
}

function ConnectionCard({
    provider,
    connection,
    syncing,
    onSync,
    onDisconnect,
    onReconnect,
    onManageScope,
}: {
    provider: SaasProvider;
    connection: SaasConnection;
    syncing: boolean;
    onSync: () => void;
    onDisconnect: () => void;
    onReconnect: () => void;
    onManageScope?: () => void;
}) {
    const health = connectionHealth(connection);
    const ProviderIcon = PROVIDERS[provider].icon;
    const avatar = typeof connection.details?.avatarUrl === "string" ? connection.details.avatarUrl : null;
    const counts = connection.details ?? {};
    const expired = connection.credentialState === "EXPIRED";

    return (
        <Card className="cloud-list-card rounded-2xl" data-testid={`card-saas-connection-${connection.id}`}>
            <CardContent className="p-4">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {avatar ? <img src={avatar} alt="" className="w-full h-full object-cover" /> : <ProviderIcon className="w-5 h-5 text-primary" />}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                            <h3 className="font-semibold text-sm truncate">{accountDisplay(connection)}</h3>
                            <StatusPill tone={health.tone} label={health.label} title={health.detail} />
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <MetaTag>{humanize(connection.accountType)}</MetaTag>
                            <MetaTag>{connection.instanceUrl.replace(/^https?:\/\//, "")}</MetaTag>
                            {connection.logSource && <MetaTag>{LOG_SOURCE_LABELS[connection.logSource]}</MetaTag>}
                            {connection.authType === "ACCESS_TOKEN" && <MetaTag>Access token</MetaTag>}
                            {connection.customApp && <MetaTag>Custom app</MetaTag>}
                            {connection.repositorySelection && <MetaTag>{humanize(connection.repositorySelection)} repositories</MetaTag>}
                            {provider === "gitlab" && (
                                <MetaTag>{connection.selectedGroupIds.length ? `${connection.selectedGroupIds.length} groups selected` : "All accessible groups"}</MetaTag>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {expired ? (
                            <EndpointActionButton className="h-[34px] px-3 rounded-[10px] text-[12.5px]" onClick={onReconnect}>
                                <Link2 className="w-4 h-4" />
                                Reconnect
                            </EndpointActionButton>
                        ) : (
                            <Button variant="outline" className="saas-small-button" onClick={onSync} disabled={syncing || connection.syncRunning}>
                                <RefreshCw className={`w-4 h-4 ${syncing || connection.syncRunning ? "animate-spin" : ""}`} />
                                Sync now
                            </Button>
                        )}
                        {onManageScope && (
                            <Button variant="outline" className="saas-small-button" onClick={onManageScope}>
                                <FolderTree className="w-4 h-4" />
                                Scope
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            className="saas-small-button border-destructive/30 text-destructive hover:bg-destructive/5 hover:text-destructive"
                            onClick={onDisconnect}
                        >
                            <Trash2 className="w-4 h-4" />
                            Disconnect
                        </Button>
                    </div>
                </div>

                <div className="mt-3 pt-3 border-t grid grid-cols-2 md:grid-cols-5 gap-4">
                    <KeyValue label="Connected">{formatRelative(connection.connectedAt)}{connection.connectedBy ? ` by ${connection.connectedBy}` : ""}</KeyValue>
                    <KeyValue label="Last inventory sync">{formatDateTime(connection.syncEndedAt, connection.syncRunning ? "In progress" : "—")}</KeyValue>
                    <KeyValue label="Log history">{connection.historicalCompletedAt ? `Collected ${formatRelative(connection.historicalCompletedAt)}` : "Pending"}</KeyValue>
                    <KeyValue label="Logs collected until">{formatDateTime(connection.logsCollectedUntil, "Pending")}</KeyValue>
                    <KeyValue label="Inventory">
                        {`${Number(counts.identityCount ?? 0).toLocaleString()} identities · ${Number(counts.resourceCount ?? 0).toLocaleString()} resources`}
                    </KeyValue>
                </div>

                {(health.tone === "error" || health.tone === "warning") && (
                    <div className={`mt-3 rounded-lg border px-3 py-2 text-xs ${health.tone === "error"
                        ? "border-red-200 bg-red-50 text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300"
                        : "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300"}`}>
                        {health.detail}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function GitlabTokenCard({ tenantName, onConnected }: { tenantName: string; onConnected: () => void }) {
    const { toast } = useToast();
    const [instanceUrl, setInstanceUrl] = useState("");
    const [token, setToken] = useState("");
    const mutation = useMutation(() => saasApi.connectGitlabToken(tenantName, instanceUrl.trim(), token.trim()), {
        onSuccess: (result) => {
            toast({ title: "GitLab connected", description: result.message });
            setToken("");
            onConnected();
        },
        onError: (error) =>
            void toast({ title: "Unable to connect GitLab", description: saasErrorMessage(error, "Check the URL and token."), variant: "destructive" }),
    });

    return (
        <SectionCard title="Self-managed GitLab or token" subtitle="Use a group or personal access token with read_api" icon={<KeyRound className="w-4 h-4" />}>
            <form
                className="space-y-3"
                onSubmit={(event) => {
                    event.preventDefault();
                    mutation.mutate();
                }}
            >
                <div className="space-y-1.5">
                    <Label htmlFor="gitlab-instance">Instance URL</Label>
                    <Input id="gitlab-instance" className="saas-control" placeholder="https://gitlab.example.com" value={instanceUrl}
                           onChange={(event) => setInstanceUrl(event.target.value)} />
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor="gitlab-token">Access token</Label>
                    <Input id="gitlab-token" type="password" autoComplete="off" className="saas-control" placeholder="glpat-…" value={token}
                           onChange={(event) => setToken(event.target.value)} />
                </div>
                <EndpointActionButton type="submit" className="w-full h-[42px] justify-center" disabled={!tenantName || !token.trim() || mutation.isLoading}>
                    {mutation.isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />}
                    Connect with token
                </EndpointActionButton>
            </form>
        </SectionCard>
    );
}

function GitlabScopeDialog({
    tenantName,
    connection,
    onClose,
    onSaved,
}: {
    tenantName: string;
    connection: SaasConnection;
    onClose: () => void;
    onSaved: () => void;
}) {
    const { toast } = useToast();
    const [selected, setSelected] = useState<Set<string>>(new Set(connection.selectedGroupIds));
    const [search, setSearch] = useState("");
    const groupsQuery = useQuery(["saas-gitlab-groups", tenantName, connection.id], () => saasApi.gitlabGroups(tenantName, connection.id));
    const save = useMutation(() => saasApi.updateGitlabScope(tenantName, connection.id, [...selected]), {
        onSuccess: () => {
            toast({ title: "Scope updated", description: "A sync has started with the new scope." });
            onSaved();
        },
        onError: (error) => void toast({ title: "Unable to update the scope", description: saasErrorMessage(error, "Try again."), variant: "destructive" }),
    });

    const groups = (groupsQuery.data ?? []).filter((group: SaasScopeOption) =>
        `${group.fullPath ?? ""} ${group.name ?? ""}`.toLowerCase().includes(search.toLowerCase()));

    const toggle = (id: string) =>
        setSelected((current) => {
            const next = new Set(current);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Monitored groups</DialogTitle>
                    <DialogDescription>
                        Select the groups to monitor. Leave everything unselected to monitor every group this account can access.
                        Subgroups of a selected group are included.
                    </DialogDescription>
                </DialogHeader>
                <Input className="saas-control" placeholder="Search groups…" value={search} onChange={(event) => setSearch(event.target.value)} />
                <div className="max-h-80 overflow-y-auto space-y-1 pr-1">
                    {groupsQuery.isLoading ? (
                        <div className="py-8 flex justify-center">
                            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                        </div>
                    ) : groupsQuery.isError ? (
                        <p className="text-sm text-destructive py-4">{saasErrorMessage(groupsQuery.error, "Unable to load groups")}</p>
                    ) : groups.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-4 text-center">No groups found</p>
                    ) : (
                        groups.map((group) => (
                            <label key={group.id}
                                   className="flex items-center gap-3 rounded-lg border border-border px-3 py-2 cursor-pointer portal-card-hover">
                                <Checkbox checked={selected.has(group.id)} onCheckedChange={() => toggle(group.id)} />
                                <span className="min-w-0 flex-1">
                                    <span className="block text-sm font-medium truncate">{group.name}</span>
                                    <span className="block text-xs text-muted-foreground truncate">{group.fullPath}</span>
                                </span>
                                {group.visibility && <MetaTag>{humanize(group.visibility)}</MetaTag>}
                            </label>
                        ))
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" className="saas-outline-button" onClick={onClose}>
                        Cancel
                    </Button>
                    <EndpointActionButton className="h-[42px]" onClick={() => save.mutate()} disabled={save.isLoading}>
                        {save.isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                        Save and sync
                    </EndpointActionButton>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
